# Closing a job ad

Job ads automatically close on the date specified in their [`postingInstructions.end` field].
You can use [`closePostedPositionProfile`](#closepostedpositionprofile) to close a job ad before its end date.

See the [job ad lifecycle documentation] for more details.

## Operations

### closePostedPositionProfile

The [`closePostedPositionProfile` mutation] closes an existing posted [`PositionProfile`].

The job ad will be removed from the job board and no refund will be issued.
The [`PositionProfile`] and its associated candidate applications will be available for 180 days after its close date.

A `FORBIDDEN` error will be returned if your software attempts to close a job ad that has already been closed.
It’s recommended that you update your software’s internal state based on [`PositionProfileClosed` events](#positionprofileclosed).
Review the [job ad lifecycle documentation] for further information.

```graphql
mutation ($input: ClosePostedPositionProfileInput!) {
  closePostedPositionProfile(input: $input) {
    positionProfile {
      profileId {
        value
      }
    }
  }
}
```

```json
{
  "input": {
    "positionProfile": {
      "profileId": "seekAnzPublicTest:positionProfile:jobAd:2782PZfXV"
    }
  }
}
```

```json
{
  "closePostedPositionProfile": {
    "positionProfile": {
      "profileId": {
        "value": "seekAnzPublicTest:positionProfile:jobAd:2782PZfXV"
      }
    }
  }
}
```

## Events

### PositionProfileClosed

[`PositionProfileClosed` events] are emitted when job ads have been closed.
You will receive events for SEEK hirers you have a [`JobPosting` relationship] with at the time the job ad closes.

It’s recommended that you update your internal state based on `PositionProfileClosed` events instead of scheduled end dates.
This ensures your software remains synchronised with SEEK, particularly when a hirer contacts our Customer Service team to close a job ad early.

A `PositionProfileClosed` event is typically sent within a minute of a successful [`closePostedPositionProfile`](#closepostedpositionprofile) operation.
By contrast, scheduled closure of a job ad based on its end date is not instantaneous;
it may take up to 24 hours to take effect and for an event to be sent.

This is an example of a [webhook](/events/webhooks) body containing a `PositionProfileClosed` event:

```jsonc sample-webhook-body
{
  "events": [
    {
      "id": "seekAnzPublicTest:event:events:5dEQGJWnR6mSyUwnL14VGA",
      "type": "PositionProfileClosed",
      // The date the job ad was closed
      "createDateTime": "2019-09-20T21:02:27.101Z",
      // This can be passed to the `positionProfile` query to retrieve the job ad's final state
      "positionProfileId": "seekAnzPublicTest:positionProfile:jobAd:26d9DVAzs",
      // This is only available for signed webhook subscriptions
      "hirerId": "seekAnzPublicTest:organization:seek:93WyyF1h"
    }
  ],
  "subscriptionId": "seekAnzPublicTest:webhookSubscription:events:9H6GD9v2o8hQw7aBBG2sAU",
  "url": "https://example.com/webhook",
  "expireDateTime": "2019-09-13T22:16:10.593Z"
}
```

Once you receive an event, you can use its [`positionProfileId`] to query the current state of the job ad from the [`positionProfile` query].

You should treat a closure as final to handle incidental and intentional [event duplication and redelivery].
A [`PositionProfilePosted` event] or [`PositionProfileUpdated` event] that is delivered out of order must not re-open the job ad in your software.

[`closePostedPositionProfile` mutation]: /schema/mutation/closePostedPositionProfile
[`JobPosting` relationship]: /auth/hirer-relationships
[`positionProfile` query]: /use-cases/job-posting/managing-job-ads/querying-a-job-ad#positionprofile
[`positionProfile`]: /schema/named-type/PositionProfile
[`PositionProfileClosed` events]: /schema/named-type/PositionProfileClosedEvent
[`positionProfileId`]: /schema/webhooks/PositionProfileClosedEvent#/property/positionProfileId
[`PositionProfilePosted` event]: /use-cases/job-posting/managing-job-ads/posting-a-job-ad#positionprofileposted
[`PositionProfileUpdated` event]: /use-cases/job-posting/managing-job-ads/updating-a-job-ad#positionprofileupdated
[`postingInstructions.end` field]: /schema/named-type/PostingInstruction#/field/end
[event duplication and redelivery]: /events/webhooks#duplicates
[job ad lifecycle documentation]: /use-cases/job-posting/key-concepts#job-ad-lifecycle
